import {
  FcAngleRange,
  FcArea,
  FcCenterText,
  FcPoint,
  FcPointInCircle,
  FcPolygon,
  FcPositionProperty,
  FcReservedArea,
  FcSvg,
  FcText,
  FcTextNode,
  fcBuildColorStringFromAttribs,
  fcBuildSvgColorAttribs,
  getRotatedQuad
} from '@dtx-company/flowcode-generator-types/src'
import { TextToSVG } from './texttosvg'
import { fcFetchUrl } from './fc_fetch'
import { fcGenerateGradient } from './fc_gradient'
import { isNode, isTestEnv } from './fc_env'

const defaultFont = 'inter_black'
const centerTextClipPadding = 10
const maxOffset = 999999

function isAscii(text: string): boolean {
  return /^[\x20-\x7F]*$/.test(text)
}

const loadedTextToSVG = new Map<string, TextToSVG>()
async function fcLoadFont(name: string, ttfFile: string): Promise<TextToSVG> {
  if (loadedTextToSVG.has(name) === false) {
    let textToSVG: TextToSVG

    if (isNode() || isTestEnv) {
      // nodejs
      textToSVG = TextToSVG.loadSync(ttfFile)
    } else {
      // browser
      const url = 'https://cdn.flowcode.com/cdn/' + ttfFile
      const response = await fcFetchUrl(url, 'arraybuffer')
      textToSVG = TextToSVG.parse(response.data)
    }
    loadedTextToSVG.set(name, textToSVG)
  }
  return loadedTextToSVG.get(name)!
}

export function fcLoadFonts(textNodes: FcTextNode[] = []): Promise<unknown> {
  return Promise.all(
    textNodes.map(textNode => {
      const mappedFont = fcMapFont(textNode)
      return fcLoadFont(mappedFont, `${mappedFont}.ttf`)
    })
  )
}

export async function fcGetFont(textNode: FcTextNode): Promise<TextToSVG> {
  const fontName = fcMapFont(textNode)
  try {
    return await fcLoadFont(fontName, `${fontName}.ttf`)
  } catch {
    throw new Error('font ' + fontName + ' not loaded')
  }
}

const fontRemap: Record<string, string> = {
  centra_light: 'inter_medium',
  centra_black: 'inter_black'
}
const asciiFontMap: Record<string, string> = {
  inter_black: 'inter_black-ascii',
  inter_bold: 'inter_bold-ascii',
  inter_extrabold: 'inter_extrabold-ascii',
  inter_extralight: 'inter_extralight-ascii',
  inter_light: 'inter_light-ascii',
  inter_medium: 'inter_medium-ascii',
  inter_regular: 'inter_regular-ascii',
  inter_semibold: 'inter_semibold-ascii',
  inter_thin: 'inter_thin-ascii'
}

function fcMapFont({ font: fontName = defaultFont, text }: FcTextNode): string {
  const remap = fontRemap[fontName]
  if (remap) {
    fontName = remap
  }
  if (!fontName) {
    return ''
  }

  // if the full font has already been loaded, don't evaluate for a subset font
  if (!loadedTextToSVG.has(fontName)) {
    const asciiFontName = asciiFontMap[fontName]
    if (asciiFontName && isAscii(text)) {
      fontName = asciiFontName
    }
  }
  return fontName
}

function chordToArc(chord: number, radius: number): number {
  return 2 * Math.asin(chord / (2 * radius))
}

function addLetter(
  useCmyk: boolean,
  cx: number,
  cy: number,
  textToSVG: TextToSVG,
  text: FcText,
  radius: number,
  theta: number,
  slantAngle: number,
  radiusPad: number,
  reserved: FcReservedArea,
  position: FcPositionProperty,
  gradient?: string,
  reservedPaddingHorizontal?: number,
  reservedPaddingVertical?: number
): string {
  const x = cx + radius * Math.cos(theta)
  const y = cy + radius * Math.sin(theta)
  let attributes: any = {}
  if (gradient) {
    attributes = { fill: gradient, stroke: gradient }
  } else {
    const colorAttribs = fcBuildSvgColorAttribs(useCmyk, text.fontColor)
    const colorStr = fcBuildColorStringFromAttribs(colorAttribs)
    attributes = { fill: colorStr, stroke: colorStr }
    if (colorAttribs.opacity) {
      attributes['fill-opacity'] = colorAttribs.opacity
      attributes['stroke-opacity'] = colorAttribs.opacity
    }
  }
  // so it looks the same as when we used to scale
  attributes['stroke-width'] = 0.5
  if (text.fontOutlineColor && text.fontOutlineSize) {
    const outlineColorAttribs = fcBuildSvgColorAttribs(useCmyk, text.fontOutlineColor)
    const outlineColorStr = fcBuildColorStringFromAttribs(outlineColorAttribs)
    attributes['stroke'] = outlineColorStr
    attributes['stroke-width'] = text.fontOutlineSize
  }
  const rotate = (slantAngle * 180) / Math.PI
  const options = {
    x: x,
    y: y,
    fontSize: text.fontSize,
    anchor: 'center,middle',
    attributes: attributes,
    rotate: rotate,
    rotateX: x,
    rotateY: y
  }
  const data = textToSVG.getPath(text.text, options)
  const mh = data.box.y2 - data.box.y1
  const origin = new FcPoint(x, y)
  let padUpper = 0
  let padLower = 0

  // We divide padding by 2 to be compatible with old scale code
  const padLeftRight = (reservedPaddingHorizontal || 0) / 2
  if (reservedPaddingVertical !== undefined) {
    padUpper = FcPositionProperty.BOTTOM ? reservedPaddingVertical / 2 : mh
    padLower = FcPositionProperty.BOTTOM ? mh : reservedPaddingVertical / 2
  }
  const points = getRotatedQuad(
    new FcArea(
      data.box.x1 - padLeftRight,
      data.box.y1 - padUpper,
      data.box.x2 - data.box.x1 + 2 * padLeftRight,
      data.box.y2 - data.box.y1 + padUpper + padLower
    ),
    origin,
    slantAngle,
    1
  )

  reserved.addPolygon(new FcPolygon([points[0], points[1], points[2], points[3]]))
  return data.path
}

export function fcRenderGetUnusedPoints(
  container: FcSvg,
  cx: number,
  cy: number,
  radius: number,
  minSpacing: number,
  reserved: FcReservedArea
): FcPointInCircle[] {
  const points: FcPointInCircle[] = []

  const slantCorrection = -Math.PI / 2
  minSpacing = chordToArc(minSpacing, radius)
  const angles = reserved.angles
  if (!angles.length) {
    angles.push(new FcAngleRange(0, 0.001))
  }
  const len = angles.length
  for (let i = 0; i < len; i++) {
    const angle = angles[i]
    const startTheta = angle.start > angle.end ? angle.start : angle.end
    const endAngle = i < len - 1 ? angles[i + 1] : angles[0]
    let endTheta = endAngle.start < endAngle.end ? endAngle.start : endAngle.end
    while (endTheta < startTheta) {
      endTheta += Math.PI * 2
    }
    const diff = endTheta - startTheta
    let numSeg = Math.round(diff / minSpacing)
    const spacing = diff / numSeg
    let theta = startTheta < endTheta ? startTheta : endTheta
    numSeg++
    for (let j = 0; j < numSeg; j++) {
      const x = cx + radius * Math.cos(theta)
      const y = cy + radius * Math.sin(theta)
      const slantAngle = theta + slantCorrection
      const rotate = (slantAngle * 180) / Math.PI
      points.push(new FcPointInCircle(x, y, rotate))
      theta += spacing
    }
  }
  return points
}

export async function fcRenderCircle(
  name: string,
  container: FcSvg,
  text: FcText,
  centerX: number,
  centerY: number,
  radius: number,
  reserved: FcReservedArea,
  noPad: boolean,
  reservedPaddingHorizontal?: number,
  reservedPaddingVertical?: number,
  noRender?: boolean
): Promise<void> {
  text = { ...text }
  const numNodes = text.text.length
  const options = {
    x: 0,
    y: 0,
    fontSize: text.fontSize,
    anchor: 'center,middle',
    letterSpacing: 0.05
  }
  let textToSVG: TextToSVG
  try {
    textToSVG = await fcGetFont(text)
  } catch (err) {
    throw new Error('font ' + text.font + ' not loaded')
  }
  if (!textToSVG) {
    throw new Error('fonts not loaded')
  }

  let clockwise: boolean
  let theta: number
  const radiusPad = noPad ? 0 : 15

  if (text.position.property === FcPositionProperty.BOTTOM) {
    clockwise = false
    theta = Math.PI / 2
  } else if (text.position.property === FcPositionProperty.TOP) {
    clockwise = true
    theta = (Math.PI * 3) / 2
  } else {
    throw new Error('position type not supported')
  }

  if (text.position.extraDegrees) {
    theta += (text.position.extraDegrees * Math.PI) / 180
  }
  radius -= radiusPad
  const arcs = new Array<number>(numNodes)
  const pads = new Array<number>(numNodes)
  const widths = new Array<number>(numNodes)
  let totalArc = 0
  const str = text.text
  let prevChar = ''
  for (let i = 0; i < numNodes; i++) {
    pads[i] = 0
    let ch = str.charAt(i)
    if (ch === ' ') {
      ch = 'i'
    }
    const box = textToSVG!.getBox(ch, options)
    const width = box.x2 - box.x1
    widths[i] = width
    let arc = chordToArc(width, radius)
    arcs[i] = arc
    totalArc += arc

    if (width && i) {
      const boxJoined = textToSVG!.getBox(prevChar + ch, options)
      const widthJoined = boxJoined.x2 - boxJoined.x1
      const pad = widthJoined - widths[i - 1] - width
      arc = chordToArc(pad + 0.1, radius)
      pads[i - 1] = arc
      totalArc += arc
    }
    prevChar = ch
  }

  const direction = clockwise ? 1 : -1
  const slantCorrection = clockwise ? Math.PI / 2 : -Math.PI / 2
  let thetaI = theta - (direction * totalArc) / 2

  const textRender = { ...text }

  let hasGradient = false
  if (textRender.gradient) {
    hasGradient = true
  }

  reserved.resetMinMax()
  let path = ''
  const startTheta = thetaI
  for (let i = 0; i < numNodes; i++) {
    thetaI += (direction * arcs[i]) / 2
    textRender.text = str.charAt(i)
    path += addLetter(
      container.getUseCmyk(),
      centerX,
      centerY,
      textToSVG,
      textRender,
      radius,
      thetaI,
      thetaI + slantCorrection,
      radiusPad,
      reserved,
      text.position.property,
      hasGradient ? `url(#${name})` : undefined,
      reservedPaddingHorizontal,
      reservedPaddingVertical
    )
    thetaI += (direction * arcs[i]) / 2
    thetaI += direction * pads[i]
  }
  reserved.addAngle(
    startTheta - (direction * arcs[0] * 3) / 2,
    thetaI + (direction * arcs[numNodes - 1] * 3) / 2
  )

  let colorGradient = ''
  if (hasGradient) {
    colorGradient = fcGenerateGradient(
      container.getUseCmyk(),
      textRender.gradient!,
      name,
      true,
      (reserved.minX / container.width) * 100,
      (reserved.maxX / container.width) * 100,
      (reserved.minY / container.height) * 100,
      (reserved.maxY / container.height) * 100
    )
  }

  if (noRender) {
    return
  }
  container.addToDom(colorGradient + path)
}

interface CenterData {
  path: string
  area: FcArea
  yOffset: number
}
async function renderCenterTextElement(
  x: number,
  y: number,
  text: FcText,
  reserved: FcReservedArea,
  noReserve: boolean,
  paddingHorizontal: number,
  paddingVertical: number
): Promise<CenterData> {
  const textToSVG = await fcGetFont(text)

  text = { ...text }
  const attributes: any = { fill: text.fontColor, stroke: text.fontColor }
  if (text.fontOutlineColor && text.fontOutlineSize) {
    attributes['stroke'] = text.fontOutlineColor
    attributes['stroke-width'] = text.fontOutlineSize
  }
  const options = {
    x,
    y,
    fontSize: text.fontSize,
    anchor: 'top',
    attributes: attributes,
    letterSpacing: 0.05
  }
  const data = textToSVG.getPath(text.text, options)
  const mw = data.box.x2 - data.box.x1
  const mh = data.box.y2 - data.box.y1
  if (!noReserve) {
    reserved.addBox(
      data.box.x1 - paddingHorizontal,
      data.box.y1 - paddingVertical,
      mw + paddingHorizontal * 2,
      mh + paddingVertical * 2
    )
  }
  return {
    path: data.path,
    yOffset: data.box.y1 - y,
    area: new FcArea(data.box.x1, data.box.y1, mw, mh)
  }
}

export async function fcRenderCenterText(
  centerText: FcCenterText,
  container: FcSvg,
  reserved: FcReservedArea,
  noRender?: boolean
): Promise<void> {
  let x = 0
  let y = 0
  let path = ''
  let height = 0
  let totalHeight = 0
  let yOffset = maxOffset
  const startX: number[] = []
  const yOffsets: number[] = []

  for (const text of centerText.text) {
    if (text.text === '\n') {
      y += height + text.fontSize / 5
      height = 0
      startX.push(container.width / 2 - x / 2)
      x = 0
      yOffsets.push(yOffset)
      yOffset = maxOffset
    } else {
      const data = await renderCenterTextElement(x, y, text, reserved, true, 0, 0)
      if (data.area.height > height) {
        height = data.area.height
      }
      if (data.yOffset < yOffset) {
        yOffset = data.yOffset
      }
      x += data.area.width + text.fontSize / 10
      totalHeight = y + height
    }
  }
  startX.push(container.width / 2 - x / 2)
  yOffsets.push(yOffset)

  let row = 0
  if (centerText.heightOffset) {
    y = container.height - centerText.heightOffset - totalHeight / 2
  } else {
    y = container.height / 2 - totalHeight / 2
  }
  height = 0
  x = startX[row]
  yOffset = yOffsets[row++]
  for (const text of centerText.text) {
    if (text.text === '\n') {
      y += height + text.fontSize / 5
      height = 0
      x = startX[row]
      yOffset = yOffsets[row++]
    } else {
      const data = await renderCenterTextElement(
        x,
        y - yOffset,
        text,
        reserved,
        false,
        centerText.paddingHorizontal,
        centerText.paddingVertical
      )
      path += data.path
      if (data.area.height > height) {
        height = data.area.height
      }
      if (x <= centerTextClipPadding) {
        container.centerTextClipped = true
      }
      x += data.area.width + text.fontSize / 10
      if (x >= container.width - centerTextClipPadding) {
        container.centerTextClipped = true
      }
      if (data.area.width > container.centerTextWidthMax) {
        container.centerTextWidthMax = data.area.width
      }
    }
  }
  if (noRender) {
    return
  }
  container.addToDom(path)
}
